import React, { FC } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { PageStatusBatchProgressResponse, PageStatusResponse } from '@/declarations/models/PageStatusResponse';
import { RESPONSE_TIME_WARNING_LIMIT_MS } from '../pageStatusConstants';
import { PageStatusResult } from '../pageStatusTypes';
import {
  getAttentionCount,
  getAttentionResults,
  getMaxResponseTime,
  getSlowestResults,
  getSlowResponseCount,
} from '../pageStatusUtils';
import { PageStatusCompactResultTable } from './PageStatusCompactResultTable';
import { PageStatusMetricCard } from './PageStatusMetricCard';
import { PageStatusSectionTitle } from './PageStatusSectionTitle';
import { PageStatusStatusGroupCards } from './PageStatusStatusGroupCards';

type PageStatusDashboardProps = {
  pageStatus: PageStatusResponse;
  isRunning: boolean;
  progress: PageStatusBatchProgressResponse | null;
};

export const PageStatusDashboard: FC<PageStatusDashboardProps> = ({ pageStatus, isRunning, progress }) => {
  const { summary } = pageStatus;
  const results = pageStatus.results as PageStatusResult[];

  const attentionCount = getAttentionCount(summary);
  const attentionResults = getAttentionResults(results);
  const slowestResults = getSlowestResults(results);
  const maxResponseTime = getMaxResponseTime(results);
  const slowResponseCount = getSlowResponseCount(results);

  const checkedCount = progress?.checked ?? results.length;
  const totalCount = progress?.total || summary.total_urls;
  const hasStarted = checkedCount > 0;
  const isCompleted = !isRunning && totalCount > 0 && checkedCount >= totalCount;

  let alertSeverity: 'success' | 'warning' | 'info';
  if (attentionCount > 0) {
    alertSeverity = 'warning';
  } else if (isCompleted) {
    alertSeverity = 'success';
  } else {
    alertSeverity = 'info';
  }

  const alertText = (() => {
    if (!hasStarted) {
      return 'Venter på første batch med resultater.';
    }

    if (!isCompleted && attentionCount === 0) {
      return `Tester fortsatt. Ingen feil funnet så langt (${checkedCount} av ${totalCount}).`;
    }

    if (!isCompleted && attentionCount > 0) {
      return `Tester fortsatt. ${attentionCount} side${
        attentionCount === 1 ? '' : 'r'
      } trenger oppmerksomhet så langt.`;
    }

    if (attentionCount === 0) {
      return `Alle ${totalCount} sider returnerte vellykket statuskode. P95 var ${summary.times.p95_response_time_ms} ms, og ${slowResponseCount} sider brukte over ${RESPONSE_TIME_WARNING_LIMIT_MS} ms.`;
    }

    return `${attentionCount} av ${totalCount} sider trenger oppmerksomhet.`;
  })();

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1400 }, minWidth: 0 }}>
      <Alert severity={alertSeverity} sx={{ mb: 2, borderRadius: 2 }}>
        {alertText}
      </Alert>

      <Box sx={{ mb: 3 }}>
        <PageStatusSectionTitle title='Nøkkeltall' helper='Kort oppsummering av status og ytelse for testen.' />

        <Stack direction='row' spacing={2} useFlexGap sx={{ flexWrap: 'wrap', minWidth: 0 }}>
          <PageStatusMetricCard
            label='Testet'
            value={totalCount > 0 ? `${checkedCount} / ${totalCount}` : checkedCount}
            helper='Sider'
            accentColor='#2979FF'
          />

          <PageStatusMetricCard
            label='Trenger oppmerksomhet'
            value={attentionCount}
            helper='Final status: 3xx / 4xx / 5xx / N/A'
            accentColor={attentionCount === 0 ? '#8BC34A' : '#FF9100'}
          />

          <PageStatusMetricCard
            label='Gjennomsnitt'
            value={`${summary.times.average_response_time_ms} ms`}
            helper='Respons'
            accentColor='#00E676'
          />

          <PageStatusMetricCard
            label='P90'
            value={`${summary.times.p90_response_time_ms} ms`}
            helper='90% raskere enn dette'
            accentColor='#7C4DFF'
          />

          <PageStatusMetricCard
            label='P95'
            value={`${summary.times.p95_response_time_ms} ms`}
            helper='95% raskere enn dette'
            accentColor='#651FFF'
          />

          <PageStatusMetricCard
            label='Maks'
            value={`${maxResponseTime} ms`}
            helper='Tregeste respons'
            accentColor='#FF9100'
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 3 }}>
        <PageStatusSectionTitle title='Ekstra ytelse' helper='Mer detaljerte tall som kan brukes ved feilsøking.' />

        <Stack direction='row' spacing={2} useFlexGap sx={{ flexWrap: 'wrap', minWidth: 0 }}>
          <PageStatusMetricCard
            label='Median'
            value={`${summary.times.median_response_time_ms} ms`}
            helper='50% raskere enn dette'
            accentColor='#00BFA5'
          />

          <PageStatusMetricCard
            label={`Trege > ${RESPONSE_TIME_WARNING_LIMIT_MS} ms`}
            value={slowResponseCount}
            helper='Kan være verdt å sjekke'
            accentColor={slowResponseCount === 0 ? '#8BC34A' : '#FFA726'}
          />

          <PageStatusMetricCard
            label='Varighet'
            value={`${summary.times.elapsed_time_seconds} s`}
            helper='Total testtid'
            accentColor='#2979FF'
          />
        </Stack>
      </Box>

      <PageStatusStatusGroupCards summary={summary} />

      <PageStatusCompactResultTable
        title='Sider som trenger oppmerksomhet'
        helper='Viser sider hvor endelig status ikke er 2xx.'
        results={attentionResults}
        includeDetails
      />

      <PageStatusCompactResultTable
        title='Tregeste sider'
        helper='Viser de 5 tregeste sidene i denne testen.'
        results={slowestResults}
      />
    </Box>
  );
};
